import {html, LitElement} from 'lit';
import {live} from 'lit/directives/live.js';
import {customElement, state} from 'lit/decorators.js';

import styles from './bmi.styles';
import elements from '../../ts/css/elements.styles';
import {resetStyles} from '../../ts/css/resets.styles';

import {PreferenceData} from './preferenceData';
import {iconsFont} from '../../ts/css/icons.styles';

@customElement('ui-bmi')
export class Bmi extends LitElement {
  static readonly styles = [resetStyles, iconsFont, elements, styles];

  @state() private sliderHeight = 166;
  @state() private sliderWeight = 0;
  @state() private bmi = 0;
  @state() private showExplanation = false;
  @state() private genderSelection = 'woman';
  @state() private resultColor = '';
  @state() private resultTitle = '';
  @state() private resultExplanation = '';

  private readonly bmiMin = 16;
  private readonly bmiMax = 40;
  private readonly spriteWidth = 4900;
  private readonly spriteDelta = 98;

  handleGenderSelection(event: Event) {
    const htmlElement = event.target as HTMLInputElement;
    this.genderSelection = htmlElement.value;
    this.updateBmiAndPreference();
  }

  handleInputData(event: Event) {
    const inputElement = event.target as HTMLInputElement;

    switch (inputElement.name) {
      case 'height-text-input':
      case 'height-slider-input':
        this.sliderHeight = parseFloat(inputElement.value.replace(',', '.'));
        break;
      case 'weight-text-input':
      case 'weight-slider-input':
        this.sliderWeight = parseFloat(inputElement.value.replace(',', '.'));
        break;
    }

    this.updateBmiAndPreference();
  }

  handleBmiSliderInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const sliderBmi = parseFloat(inputElement.value);

    this.sliderWeight = Math.round(sliderBmi * Math.pow((this.sliderHeight / 100), 2));
    this.updateBmiAndPreference();
  }

  updateBmiAndPreference() {
    this.bmi = this.getCalculatedBmi(this.sliderWeight, this.sliderHeight);

    const preference = PreferenceData.getPreferenceForBMI(this.bmi, this.genderSelection === 'woman');
    this.resultColor = preference.color;
    this.resultTitle = preference.title;
    this.resultExplanation = preference.explanation;
  }

  getCalculatedBmi(kg: number, cm: number): number {
    if (kg <= 0 || cm <= 0) {
      return 0;
    } else {
      return kg / Math.pow((cm / 100), 2);
    }
  }

  getFigureImgX(): number {
    let currentFrame = (this.bmi - this.bmiMin) * 2;

    if (this.genderSelection === 'woman') {
      currentFrame = currentFrame + 2;
    }

    const newX = Math.floor(currentFrame) * -this.spriteDelta;
    if (newX <= 0 && newX >= (this.spriteDelta - this.spriteWidth)) {
      return newX;
    } else if (newX < (this.spriteDelta - this.spriteWidth)) {
      return this.spriteDelta - this.spriteWidth;
    }
    return 0;
  }

  getBmiTriangleSliderPosition() {
    const x = (this.bmi - this.bmiMin) / (this.bmiMax - this.bmiMin);
    if (x > 1) {
      return '100%';
    } else if (x > 0) {
      return (x * 100) + '%';
    } else {
      return '0%';
    }
  }

  toggleExplanation() {
    this.showExplanation = !this.showExplanation;
  }

  firstUpdated() {
    window.setTimeout(() => {
      const start = new Date();
      const targetValue = 66;
      const duration = 3000;
      const startVal = 0;
      const ease = 5;
      const difference = targetValue - startVal;

      const intervalId = window.setInterval(() => {
        let progress = ((new Date()).valueOf() - start.valueOf()) / duration;
        let nextVal;

        if (progress > 1) {
          progress = 1;
        }

        if (progress < 0.5) {
          nextVal = startVal + difference * Math.pow(2 * progress, ease) / 2;
        } else {
          nextVal = startVal + difference * (2 - Math.pow(2 * (1 - progress), ease)) / 2;
        }

        this.sliderWeight = Math.round(nextVal);
        this.updateBmiAndPreference();

        if (progress >= 1) {
          clearInterval(intervalId);
        }
      }, 10);
    }, 250);
  }

  render() {
    return html`
      <div>
        <div>
            <input id="woman" type="radio" class="custom-radio" value="woman" name="genderSelection"
                   ?checked=${this.genderSelection === 'woman'} @change=${this.handleGenderSelection} />
            <label for="woman" class="gender-selection-woman">Frau</label>
            <input id="men" type="radio" class="custom-radio" value="men" name="genderSelection"
                   ?checked=${this.genderSelection === 'men'} @change=${this.handleGenderSelection} />
            <label for="men">Mann</label>
        </div>

        ${this.getHeightSliderTemplate()}
        ${this.getWeightSliderTemplate()}

        <div class="mod-slider result">
          <div class="left-box">
            <div class="current-value" style="background-color: ${this.resultColor};">
              <input type="text" class="value-field" .value="${live(this.bmi.toFixed(1))}" readonly>

              <div class="tooltip-picker-wrapper">
                <div class="tooltip-picker-innerwrap">
                  <svg class="triangle" style="left: ${this.getBmiTriangleSliderPosition()};" height="10" width="20">
                    <polygon points="0,0 20,0 10,10" style="fill:${this.resultColor};" />
                  </svg>
                </div>
              </div>
            </div>

            <span class="child label">Ihr BMI: </span>

            <div class="slider">
              <span class="color-track track-left"></span>
              <span class="color-track"></span>
              <input type="range"
                     tabindex="-1"
                     min="${this.bmiMin}"
                     max="${this.bmiMax}"
                     step="0.1"
                     .value="${live(this.bmi.toFixed(1))}"
                     @input=${this.handleBmiSliderInput} />
            </div>
            <p class="statement" @click=${this.toggleExplanation}>
              <span>${this.resultTitle}</span>
              <i class="icon icon-info"></i>
            </p>
          </div>

          <div class="figure right-box" style="background-position: ${this.getFigureImgX()}px 0;">
          </div>

          <div class="text-result">
            <p class="${this.showExplanation ? 'explanation ng-hide-add' : 'explanation ng-hide-remove'}">${this.resultExplanation}</p>
          </div>

        </div>
      </div>
    `;
  }

  getHeightSliderTemplate() {
    return html`
      <div class="mod-slider">
        <div class="current-value">
          <input name="height-text-input"
                 type="text"
                 class="value-field"
                 .value="${live(this.sliderHeight.toString())}"
                 @input=${this.handleInputData} />
        </div>
        <h4 class="label"> Ihre Größe </h4>

        <p class="comment">in cm</p>
        <div class="slider">
          <span class="color-track track-left"></span>
          <span class="color-track"></span>
          <input name="height-slider-input"
                 type="range"
                 tabindex="-1"
                 min="50"
                 max="250"
                 step="0.5"
                 .value="${live(this.sliderHeight.toString())}"
                 @input=${this.handleInputData} />
        </div>
      </div>
    `;
  }

  getWeightSliderTemplate() {
    return html`
      <div class="mod-slider">
        <div class="current-value">
          <input name="weight-text-input"
                 type="text"
                 class="value-field"
                 .value="${live(this.sliderWeight.toString())}"
                 @input=${this.handleInputData} />

        </div>
        <span class="label"> Ihr Gewicht </span>

        <p class="comment">in kg</p>
        <div class="slider">
          <span class="color-track track-left"></span>
          <span class="color-track"></span>
          <input name="weight-slider-input"
                 type="range"
                 tabindex="-1"
                 min="10"
                 max="200"
                 step="0.5"
                 .value="${live(this.sliderWeight.toString())}"
                 @input=${this.handleInputData} />
        </div>
      </div>
    `;
  }
}
